'use client';

import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { isEmpty } from 'lodash';

import Header from 'src/components/layouts/header';
import Sidebar from 'src/components/layouts/sidebar';
import CustomerServiceAgreementModal from 'src/components/shared/modals/customer-service-agreement-modal';
import NewFeaturesIntroModal from 'src/components/shared/modals/new-features-intro-modal';
import FullStoryUserIdentifier from 'src/components/fullstory-user-identifier';
import DatadogUserIdentifier from 'src/components/datadog-user-identifier';

import {
	LookupProvider,
	LookupContext,
} from 'src/context/lookup-context/lookup-context';
import {
	ADMIN_LOGIN_AS_CUSTOMER_KEY,
	CUSTOMER_USER_ACCESS_COOKIE,
	getCookie,
	USER_ACCESS_TOKEN_KEY,
} from 'src/helpers/token';
import { updateProfile } from 'src/services/profile';
import JobDetailsSlideOutContainer from 'src/JobDetailsSlideOutContainer';
import { PusherConfigProvider } from 'src/context/pusher-context';
import {
	AuthContext,
	AuthProvider,
} from 'src/context/auth-context/auth-context';
import { PortalLaunchDarklyProvider } from 'src/components/launchdarkly-provider';
import useLaunchDarklyIdentify from 'src/hooks/use-launchdarkly-identify';
import type { UserDetails, PropertyManagerSettingItem } from 'src/types/auth';
import Modals from 'src/components/shared/modals';

const Interface = ({ children }: { children: React.ReactNode }) => {
	const [userDetails, setUserDetails] = useContext(AuthContext);
	const lookupData = useContext(LookupContext);
	const propertyManagerSettingsLookupObj =
		lookupData?.propertyManagerSettingsLookupObj;
	useLaunchDarklyIdentify();
	const [isAgreementButtonLoading, setAgreementButtonLoading] = useState(false);
	const shouldShowNewFeaturesUpdateModal = !isEmpty(
		userDetails?.active_feature_intro,
	);
	const platform = process.env.NEXT_PUBLIC_APP_ENV?.trim();
	const ShowEnvironmentBadge = platform !== 'production' && platform !== 'demo';

	const agreementHandler = () => {
		const csaAgreement = propertyManagerSettingsLookupObj?.['agree-csa'] as
			| PropertyManagerSettingItem
			| undefined;

		const payload = {
			property_manager_setting_ids: csaAgreement
				? [csaAgreement.property_manager_setting_id]
				: [],
		};

		setAgreementButtonLoading(true);
		updateProfile(payload)
			.then(() => {
				setAgreementButtonLoading(false);
				setUserDetails(
					(prevDetails: UserDetails | null) =>
						({
							...prevDetails,
							propertyManagerSettingsObj: {
								...userDetails?.propertyManagerSettingsObj,
								'agree-csa': csaAgreement,
							},
						}) as unknown as UserDetails,
				);
			})
			.catch(() => {
				setAgreementButtonLoading(false);
			});
	};

	return (
		<div className="base-container">
			{ShowEnvironmentBadge && (
				<div className={`pencil-badge badge-${platform}`}>
					<div className="environment">{platform}</div>
				</div>
			)}

			<div className="global-content">
				<Sidebar />
				<div className="global-datawrap">
					<Header />
					<div className="global-datawrap__inner">{children}</div>

					{shouldShowNewFeaturesUpdateModal && <NewFeaturesIntroModal />}

					{!isEmpty(userDetails) &&
					!userDetails?.propertyManagerSettingsObj?.['agree-csa'] ? (
						<CustomerServiceAgreementModal
							agreementHandler={agreementHandler}
							isAgreementButtonLoading={isAgreementButtonLoading}
						/>
					) : (
						''
					)}
				</div>
			</div>
		</div>
	);
};

export default function PortalLayout({
	children,
}: {
	children: React.ReactNode;
}) {
	const router = useRouter();
	const [checkingLoginStatus, setCheckingLoginStatus] = useState(false);

	useEffect(() => {
		const tokenFromCookie = getCookie(CUSTOMER_USER_ACCESS_COOKIE);
		const tokenFromLocalStorage = localStorage.getItem(USER_ACCESS_TOKEN_KEY);
		const isAdminLoginAsCustomer = localStorage.getItem(
			ADMIN_LOGIN_AS_CUSTOMER_KEY,
		);
		if (isAdminLoginAsCustomer) {
			if (!tokenFromLocalStorage) {
				router.push('/auth');
			} else {
				setCheckingLoginStatus(true);
			}
		} else if (!tokenFromCookie) {
			router.push('/auth');
		} else {
			setCheckingLoginStatus(true);
		}
	}, [router]);

	if (!checkingLoginStatus) return null;

	return (
		<AuthProvider>
			<PortalLaunchDarklyProvider>
				<FullStoryUserIdentifier />
				<DatadogUserIdentifier />
				<LookupProvider>
					<PusherConfigProvider>
						<JobDetailsSlideOutContainer />
						<Interface>{children}</Interface>
						<Modals />
					</PusherConfigProvider>
				</LookupProvider>
			</PortalLaunchDarklyProvider>
		</AuthProvider>
	);
}
